import { CalendarDays, Check } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { enGB, et } from "date-fns/locale";
import { startOfDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { fill } from "@/lib/i18n/fill";
import { selectionClasses } from "./styles";
import type { Language, Translations } from "@/lib/i18n/types";
import type { ProtocolPreset } from "@/domains/pm/utils/protocol";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface PresetOption {
  value: ProtocolPreset;
  label: string;
}

function PresetPicker({
  preset,
  copy,
  onSelect,
}: {
  preset: ProtocolPreset;
  copy: ProtocolCopy;
  onSelect: (preset: ProtocolPreset) => void;
}) {
  const options: PresetOption[] = [
    { value: "today", label: copy.today },
    { value: "this_week", label: copy.thisWeek },
    { value: "this_month", label: copy.thisMonth },
    { value: "custom", label: copy.customRange },
  ];
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
      {options.map((option) => {
        const selected = preset === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            className={cn(selectionClasses(selected), "min-h-0 items-center p-3")}
            onClick={() => onSelect(option.value)}
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium">{option.label}</span>
            {selected && <Check className="ml-auto h-4 w-4 text-primary" />}
          </button>
        );
      })}
    </div>
  );
}

function CustomRangeCalendar({
  range,
  language,
  hint,
  onSelect,
}: {
  range: DateRange | undefined;
  language: Language;
  hint: string;
  onSelect: (range: DateRange | undefined) => void;
}) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <p className="mb-2 text-xs text-muted-foreground">{hint}</p>
      <Calendar
        mode="range"
        numberOfMonths={2}
        selected={range}
        onSelect={onSelect}
        defaultMonth={range?.from ?? new Date()}
        disabled={{ after: startOfDay(new Date()) }}
        locale={language === "et" ? et : enGB}
        className="mx-auto w-fit"
      />
    </div>
  );
}

interface ProtocolDateStepProps {
  preset: ProtocolPreset;
  customRange: DateRange | undefined;
  hasCompleteRange: boolean;
  rangeLabel: string;
  /** How many issues the chosen period holds, or null while it is unknown. */
  countLabel: string | null;
  language: Language;
  copy: ProtocolCopy;
  onPresetSelect: (preset: ProtocolPreset) => void;
  onCustomRangeSelect: (range: DateRange | undefined) => void;
}

export function ProtocolDateStep(props: ProtocolDateStepProps) {
  return (
    <div className="space-y-4">
      <div>
        {/* The selected period sits on the heading line rather than in a panel
            below the calendar: with two months open, a box under them is off
            screen exactly while the user is choosing dates. aria-live because
            it changes under the user without a focus move — and the region is
            mounted unconditionally, empty and all: a screen reader announces
            changes to a region already in the accessibility tree, so one that
            appears together with its first message is never read out. That
            first message is the one that matters, the completed custom range. */}
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold">{props.copy.datesStep}</h3>
          {/* The count rides in the SAME live region as the range, rather
              than mounting its own: one announcement per change, and a region
              already in the tree is the only kind a screen reader reports. */}
          <p aria-live="polite" className="text-sm text-muted-foreground">
            {props.hasCompleteRange
              ? fill(props.copy.selectedRange, { range: props.rangeLabel })
              : ""}
            {props.hasCompleteRange && props.countLabel ? ` · ${props.countLabel}` : ""}
          </p>
        </div>
        <PresetPicker preset={props.preset} copy={props.copy} onSelect={props.onPresetSelect} />
      </div>
      {props.preset === "custom" && (
        <CustomRangeCalendar
          range={props.customRange}
          language={props.language}
          hint={props.copy.customRangeHint}
          onSelect={props.onCustomRangeSelect}
        />
      )}
    </div>
  );
}
